import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Lock, X, Eye, CheckCircle, Sparkles, Undo2, Redo2, MousePointer2, Keyboard, Trash2 } from 'lucide-react';

interface TextHighlighterProps {
  text: string;
  lockedElements: string[];
  onLockedElementsChange: (elements: string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

interface Selection {
  start: number;
  end: number;
  text: string;
}

interface HistoryAction {
  type: 'add' | 'remove';
  elements: string[];
  timestamp: number;
}

export const TextHighlighter: React.FC<TextHighlighterProps> = ({
  text,
  lockedElements,
  onLockedElementsChange,
  placeholder = "Enter text...",
  label,
  disabled = false,
}) => {
  const [currentSelection, setCurrentSelection] = useState<Selection | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [recentlyLocked, setRecentlyLocked] = useState<string[]>([]);
  const [lockConfirmation, setLockConfirmation] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryAction[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Clear states when disabled or text changes
  useEffect(() => {
    if (disabled) {
      setCurrentSelection(null);
      setIsSelecting(false);
    }
  }, [disabled]);

  useEffect(() => {
    setCurrentSelection(null);
    setIsSelecting(false);
    setLockConfirmation(null);
  }, [text]);

  // Clear recently locked state after animation
  useEffect(() => {
    if (recentlyLocked.length > 0) {
      const timer = setTimeout(() => setRecentlyLocked([]), 2000);
      return () => clearTimeout(timer);
    }
  }, [recentlyLocked]);

  // Clear lock confirmation after delay
  useEffect(() => {
    if (lockConfirmation) {
      const timer = setTimeout(() => setLockConfirmation(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [lockConfirmation]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled || !textRef.current) return;

      // Lock selection (Cmd/Ctrl + L)
      if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
        e.preventDefault();
        if (currentSelection) {
          handleLockSelection();
        }
      }

      // Undo (Cmd/Ctrl + Z)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }

      // Redo (Cmd/Ctrl + Shift + Z)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }

      // Show keyboard shortcuts (?)
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        setShowKeyboardShortcuts(true);
      }

      // Hide keyboard shortcuts (Escape)
      if (e.key === 'Escape') {
        setShowKeyboardShortcuts(false);
        setCurrentSelection(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentSelection, disabled]);

  // Add action to history
  const addToHistory = useCallback((action: HistoryAction) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(action);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Handle text selection
  const handleMouseUp = useCallback(() => {
    if (disabled || !textRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setCurrentSelection(null);
      setIsSelecting(false);
      return;
    }

    const range = selection.getRangeAt(0);
    const selectedText = selection.toString().trim();
    
    if (selectedText && textRef.current.contains(range.commonAncestorContainer)) {
      // Calculate character positions within the text
      const textContent = textRef.current.textContent || '';
      const beforeRange = document.createRange();
      beforeRange.setStart(textRef.current.firstChild || textRef.current, 0);
      beforeRange.setEnd(range.startContainer, range.startOffset);
      
      const start = beforeRange.toString().length;
      const end = start + selectedText.length;

      setCurrentSelection({
        start,
        end,
        text: selectedText
      });
      setIsSelecting(true);
    } else {
      setCurrentSelection(null);
      setIsSelecting(false);
    }
  }, [disabled]);

  // Handle mouse down
  const handleMouseDown = useCallback(() => {
    if (disabled) return;
    setIsSelecting(false);
  }, [disabled]);

  // Lock selected text
  const handleLockSelection = useCallback(() => {
    if (!currentSelection || disabled) return;

    const selectedText = currentSelection.text;
    const words = selectedText.split(/\s+/).filter(word => word.length > 0);
    const newElements = words.filter(word => !lockedElements.includes(word));
    
    if (newElements.length > 0) {
      const updatedElements = [...lockedElements, ...newElements];
      onLockedElementsChange(updatedElements);
      
      // Add to history
      addToHistory({
        type: 'add',
        elements: newElements,
        timestamp: Date.now()
      });
      
      // Show confirmation
      setRecentlyLocked(newElements);
      setLockConfirmation(newElements.length === 1 ? newElements[0] : `${newElements.length} elements`);
    }
    
    // Clear selection
    setCurrentSelection(null);
    setIsSelecting(false);
    window.getSelection()?.removeAllRanges();
  }, [currentSelection, disabled, lockedElements, onLockedElementsChange, addToHistory]);

  // Unlock individual element
  const handleUnlockElement = useCallback((elementToRemove: string) => {
    if (disabled) return;
    
    const updatedElements = lockedElements.filter(element => element !== elementToRemove);
    onLockedElementsChange(updatedElements);
    
    // Add to history
    addToHistory({
      type: 'remove',
      elements: [elementToRemove],
      timestamp: Date.now()
    });
  }, [disabled, lockedElements, onLockedElementsChange, addToHistory]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (historyIndex < 0) return;
    
    const action = history[historyIndex];
    if (action.type === 'add') {
      // Remove the elements that were added
      const updatedElements = lockedElements.filter(element => !action.elements.includes(element));
      onLockedElementsChange(updatedElements);
    } else {
      // Add back the elements that were removed
      const updatedElements = [...lockedElements, ...action.elements];
      onLockedElementsChange(updatedElements);
    }
    
    setHistoryIndex(prev => prev - 1);
  }, [history, historyIndex, lockedElements, onLockedElementsChange]);

  // Redo last undone action
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    
    const action = history[historyIndex + 1];
    if (action.type === 'add') {
      // Add the elements back
      const updatedElements = [...lockedElements, ...action.elements];
      onLockedElementsChange(updatedElements);
    } else {
      // Remove the elements again
      const updatedElements = lockedElements.filter(element => !action.elements.includes(element));
      onLockedElementsChange(updatedElements);
    }
    
    setHistoryIndex(prev => prev + 1);
  }, [history, historyIndex, lockedElements, onLockedElementsChange]);

  // Clear all locked elements
  const handleClearAllLockedElements = useCallback(() => {
    if (lockedElements.length === 0) return;
    
    // Add to history before clearing
    addToHistory({
      type: 'remove',
      elements: [...lockedElements], // Copy all current elements
      timestamp: Date.now()
    });
    
    // Clear all elements
    onLockedElementsChange([]);
    
    // Show confirmation
    setLockConfirmation(`${lockedElements.length} elements cleared`);
  }, [lockedElements, onLockedElementsChange, addToHistory]);

  // Render interactive text with selection and locked elements
  const renderInteractiveText = () => {
    if (!text.trim()) {
      return (
        <div className="text-gray-500 dark:text-gray-400 italic p-8 min-h-[160px] flex items-center justify-center 
          border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800/30">
          <div className="text-center">
            <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="font-medium">{placeholder}</p>
          </div>
        </div>
      );
    }

    const textContent = text;
    let processedText = textContent;
    
    // Create spans for each word to enable proper selection highlighting
    const words = textContent.split(/(\s+)/);
    const elements = words.map((part, index) => {
      const isWord = part.trim().length > 0 && /\w/.test(part);
      const isLocked = isWord && lockedElements.includes(part.trim());
      const isRecentlyLocked = isWord && recentlyLocked.includes(part.trim());
      
      if (isWord && isLocked) {
        return (
          <span
            key={index}
            className={`locked-word bg-emerald-200 dark:bg-emerald-800/40 text-emerald-800 dark:text-emerald-300 
              px-2 py-1 rounded-md font-semibold border border-emerald-300 dark:border-emerald-700 mx-0.5
              ${isRecentlyLocked ? 'animate-pulse ring-2 ring-emerald-400' : ''}`}
          >
            {part}
          </span>
        );
      }
      
      return <span key={index}>{part}</span>;
    });

    return (
      <div
        ref={textRef}
        className={`w-full min-h-[160px] p-8 border-2 border-blue-300 dark:border-blue-600 rounded-xl 
          bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 
          text-gray-900 dark:text-dark-text leading-relaxed text-lg cursor-text
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 dark:hover:border-blue-500'}
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent 
          transition-all duration-300 relative select-text`}
        onMouseUp={handleMouseUp}
        onMouseDown={handleMouseDown}
      >
        {elements}
        
        {/* Selection highlight overlay */}
        {currentSelection && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute bg-blue-400/20 dark:bg-blue-400/30 rounded-md animate-pulse"
                 style={{
                   left: `${(currentSelection.start / text.length) * 100}%`,
                   width: `${((currentSelection.end - currentSelection.start) / text.length) * 100}%`,
                   top: '2rem',
                   height: '1.5rem'
                 }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-10">
      {label && (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">{label}</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Select text to lock elements that stay consistent across all variations
          </p>
        </div>
      )}

      {/* Section 1: Master Description Area */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 
        border-2 border-blue-200 dark:border-blue-700 rounded-2xl p-8 shadow-lg">
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-500 rounded-full mr-4 animate-pulse"></div>
            <h3 className="text-xl font-bold text-blue-800 dark:text-blue-300">
              Master Description
            </h3>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Keyboard shortcuts button */}
            <button
              onClick={() => setShowKeyboardShortcuts(!showKeyboardShortcuts)}
              className="flex items-center space-x-2 text-sm text-blue-600 dark:text-blue-400 
                hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 
                dark:hover:bg-blue-900/20 px-3 py-2 rounded-lg transition-all duration-300 
                border border-blue-200 dark:border-blue-600"
            >
              <Keyboard className="h-4 w-4" />
              <span>Shortcuts</span>
            </button>
            
            {/* Selection info */}
            {!disabled && text.trim() && (
              <div className="flex items-center space-x-2 text-sm text-blue-600 dark:text-blue-400 
                bg-blue-100 dark:bg-blue-900/30 px-4 py-2 rounded-full border border-blue-300 dark:border-blue-600">
                <MousePointer2 className="h-4 w-4" />
                <span className="font-medium">Click and drag to select text</span>
              </div>
            )}
          </div>
        </div>

        <div className="relative">
          {renderInteractiveText()}

          {/* Lock confirmation notification */}
          {lockConfirmation && (
            <div className="absolute top-4 right-4 bg-emerald-500 text-white px-6 py-3 rounded-xl shadow-xl 
              flex items-center space-x-3 animate-bounce z-40">
              <CheckCircle className="h-5 w-5" />
              <span className="font-bold">"{lockConfirmation}" locked successfully!</span>
            </div>
          )}
        </div>

        {/* Action Bar */}
        {currentSelection && (
          <div className="mt-6 p-4 bg-white dark:bg-dark-surface rounded-xl border border-blue-200 dark:border-blue-700 
            shadow-md flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Selected: "{currentSelection.text}"
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setCurrentSelection(null);
                  setIsSelecting(false);
                  window.getSelection()?.removeAllRanges();
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 
                  hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 
                  dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
              
              <button
                onClick={handleLockSelection}
                className="bg-blue-gradient text-white px-6 py-2 rounded-lg hover:shadow-blue-glow 
                  transition-all duration-300 shadow-sm transform hover:scale-105 
                  active:scale-95 flex items-center font-semibold"
              >
                <Lock className="h-4 w-4 mr-2" />
                Lock Selection
              </button>
            </div>
          </div>
        )}

        {/* Keyboard Shortcuts Panel */}
        {showKeyboardShortcuts && (
          <div className="mt-6 bg-white dark:bg-dark-surface rounded-xl border border-blue-200 dark:border-blue-700 
            shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-blue-800 dark:text-blue-300">Keyboard Shortcuts</h4>
              <button
                onClick={() => setShowKeyboardShortcuts(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span>Lock Selection</span>
                  <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs">⌘L</kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>Undo</span>
                  <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs">⌘Z</kbd>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span>Redo</span>
                  <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs">⌘⇧Z</kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>Cancel Selection</span>
                  <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs">Esc</kbd>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 
          rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Sparkles className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-blue-800 dark:text-blue-300 mb-2">
                How to Lock Elements:
              </h4>
              <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-400">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Click and drag to select any text (words, phrases, or sentences)
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Use the "Lock Selection\" button or press ⌘L (Ctrl+L on Windows)
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Locked elements will appear in the protected section below
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Use ⌘Z to undo and ⌘⇧Z to redo lock actions
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Protected Elements with Undo/Redo */}
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 
        border-2 border-emerald-200 dark:border-emerald-700 rounded-2xl p-8 shadow-lg">
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Lock className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mr-4" />
            <h3 className="text-xl font-bold text-emerald-800 dark:text-emerald-300">
              Protected Elements ({lockedElements.length})
            </h3>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Undo/Redo buttons */}
            <button
              onClick={handleUndo}
              disabled={historyIndex < 0}
              className="flex items-center px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400 
                hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50 
                dark:hover:bg-emerald-900/20 rounded-lg transition-all duration-300 
                border border-emerald-200 dark:border-emerald-800 disabled:opacity-50 
                disabled:cursor-not-allowed"
              title="Undo last action (⌘Z)"
            >
              <Undo2 className="h-4 w-4 mr-1" />
              Undo
            </button>
            
            <button
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              className="flex items-center px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400 
                hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50 
                dark:hover:bg-emerald-900/20 rounded-lg transition-all duration-300 
                border border-emerald-200 dark:border-emerald-800 disabled:opacity-50 
                disabled:cursor-not-allowed"
              title="Redo last action (⌘⇧Z)"
            >
              <Redo2 className="h-4 w-4 mr-1" />
              Redo
            </button>
            
            {/* Clear All button */}
            {lockedElements.length > 0 && (
              <button
                onClick={handleClearAllLockedElements}
                className="flex items-center px-3 py-2 text-sm text-red-600 dark:text-red-400 
                  hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 
                  dark:hover:bg-red-900/20 rounded-lg transition-all duration-300 
                  border border-red-200 dark:border-red-800"
                title="Clear all protected elements"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear All
              </button>
            )}
            
            {lockedElements.length > 0 && (
              <div className="text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 
                px-4 py-2 rounded-full border border-emerald-300 dark:border-emerald-600 font-medium">
                🛡️ Variation-proof elements
              </div>
            )}
          </div>
        </div>

        {lockedElements.length === 0 ? (
          <div className="bg-emerald-100 dark:bg-emerald-900/30 border-2 border-dashed border-emerald-300 dark:border-emerald-600 
            rounded-xl p-12 text-center">
            <Lock className="h-16 w-16 text-emerald-400 dark:text-emerald-500 mx-auto mb-4 opacity-50" />
            <h4 className="text-lg font-bold text-emerald-700 dark:text-emerald-300 mb-2">
              No Protected Elements Yet
            </h4>
            <p className="text-emerald-600 dark:text-emerald-400 max-w-md mx-auto">
              Start by selecting text in the master description above. 
              Locked elements will appear here and remain consistent across all variations.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lockedElements.map((element, index) => (
              <div
                key={index}
                className={`group flex items-center justify-between p-6 bg-white dark:bg-dark-card 
                  border-2 border-emerald-200 dark:border-emerald-700 rounded-xl shadow-md
                  transition-all duration-500 hover:shadow-lg hover:scale-105 ${
                    recentlyLocked.includes(element) 
                      ? 'ring-4 ring-emerald-400 bg-emerald-50 dark:bg-emerald-900/40 animate-pulse' 
                      : ''
                  }`}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mr-4 flex-shrink-0 animate-pulse"></div>
                  <div className="flex-1">
                    <span className="text-lg font-bold text-emerald-800 dark:text-emerald-300 block truncate">
                      {element}
                    </span>
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 
                      px-2 py-1 rounded-full inline-block mt-1">
                      PROTECTED
                    </span>
                  </div>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleUnlockElement(element)}
                    className="p-3 text-emerald-600 dark:text-emerald-400 hover:text-red-600 
                      dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 
                      rounded-xl transition-all duration-300 flex-shrink-0 ml-4 opacity-0 
                      group-hover:opacity-100 hover:scale-110"
                    title="Remove protection"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Protected Elements Info */}
        {lockedElements.length > 0 && (
          <div className="mt-6 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 
            rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Lock className="h-6 w-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-emerald-800 dark:text-emerald-300 mb-2">
                  Protection Guarantee:
                </h4>
                <ul className="space-y-2 text-sm text-emerald-700 dark:text-emerald-400">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    These elements will remain exactly the same in all variations
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    Only non-protected parts will be modified by AI
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    Click × on any element to remove protection
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    Use "Clear All\" to remove all protections at once
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    Use ⌘Z to undo any lock actions
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom CSS for enhanced styling */}
      <style jsx>{`
        .locked-word {
          animation: lockPulse 2s ease-in-out infinite;
        }
        
        @keyframes lockPulse {
          0%, 100% { 
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
          }
          50% { 
            box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
          }
        }
        
        .dark .locked-word {
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        }
        
        /* Custom selection styling */
        ::selection {
          background: rgba(59, 130, 246, 0.3);
          color: inherit;
        }
        
        /* Selection highlight animation */
        @keyframes selectionHighlight {
          0% { opacity: 0.5; }
          50% { opacity: 0.8; }
          100% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};